import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, BarChart2, Activity, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SearchConsoleQuery {
  id?: string;
  target_site: string;
  query: string;
  date: string; // YYYY-MM-DD
  impressions: number;
  clicks: number;
  ctr: number; // 0-1
  position: number; // average position
  page: string | null;
  device: 'desktop' | 'mobile';
}

type Progress = {
  current: number;
  total: number;
  status: string;
};

const TARGET_SITES = ['halesac', 'myccs', 'abrahamac'] as const;
const DEVICES: Array<'desktop' | 'mobile'> = ['desktop', 'mobile'];

const BRAND_TERMS: Record<string, string[]> = {
  halesac: ['hales ac', 'hales air', 'hales air conditioning'],
  myccs: ['my ccs', 'ccs air', 'ccs air conditioning'],
  abrahamac: ['abraham ac', 'abraham air', 'abraham air conditioning'],
};

const URL_PATTERNS: Record<string, string[]> = {
  brand: ['/', '/about', '/contact', '/services'],
  'high-intent': ['/services/ac-repair', '/services/hvac-installation', '/services/emergency-repair', '/contact'],
  informational: ['/blog', '/blog/hvac-tips', '/resources', '/faq'],
  'long-tail': ['/blog', '/resources/guides', '/faq'],
};

const BASE_QUERIES: Array<{ query: string; type: 'brand' | 'high-intent' | 'informational' | 'long-tail'; volume: number }> = [
  // High-intent service queries
  { query: 'ac repair near me', type: 'high-intent', volume: 22000 },
  { query: 'emergency ac repair', type: 'high-intent', volume: 12000 },
  { query: 'hvac installation', type: 'high-intent', volume: 8000 },
  { query: 'air conditioner replacement', type: 'high-intent', volume: 7000 },

  // Informational queries
  { query: 'ac not blowing cold air', type: 'informational', volume: 18000 },
  { query: 'what is seer rating', type: 'informational', volume: 9000 },
  { query: 'best thermostat settings', type: 'informational', volume: 6000 },

  // Long-tail
  { query: 'how often to change hvac filter', type: 'long-tail', volume: 4000 },
  { query: 'why is my ac leaking water', type: 'long-tail', volume: 5000 },
];

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function brandMatchForSite(site: string, query: string): boolean {
  const terms = BRAND_TERMS[site] || [];
  return terms.some((t) => query.toLowerCase().includes(t));
}

function expectedPositionFor(site: string, q: { query: string; type: string }, device: 'desktop' | 'mobile'): number {
  // Brand match = top results for that site, others worse
  if (brandMatchForSite(site, q.query)) {
    const base = device === 'desktop' ? 1.5 : 2.2;
    return clamp(base + Math.random() * 1.2, 1, 4);
  }

  // Non-brand: high-intent better than informational/long-tail
  if (q.type === 'high-intent') {
    return clamp(6 + Math.random() * 12, 3, 25);
  }
  if (q.type === 'informational') {
    return clamp(12 + Math.random() * 20, 5, 40);
  }
  // long-tail
  return clamp(10 + Math.random() * 25, 5, 50);
}

function ctrFromPosition(position: number): number {
  // Rough CTR decay curve by position
  // pos1 ~30-35%, pos5 ~8-10%, pos10 ~3-4%, pos20 ~1-2%
  const ctr = 0.33 * Math.exp(-0.15 * (position - 1));
  return clamp(ctr, 0.01, 0.4);
}

function impressionsFor(volumePerMonth: number, device: 'desktop' | 'mobile'): number {
  // Convert monthly volume to daily; give device split
  const daily = volumePerMonth / 30;
  const split = device === 'mobile' ? 0.6 : 0.4;
  const jitter = 0.85 + Math.random() * 0.3; // +/- 15%
  return Math.max(1, Math.floor(daily * split * jitter));
}

function pageForType(qType: string): string {
  const arr = URL_PATTERNS[qType] || ['/'];
  return pick(arr);
}

export default function ShareOfSearchPage() {
  const [scData, setScData] = useState<SearchConsoleQuery[]>([]);
  const [loading, setLoading] = useState(true);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [progress, setProgress] = useState<Progress>({ current: 0, total: 0, status: '' });

  const [selectedSite, setSelectedSite] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<number>(30);

  useEffect(() => {
    fetchData();
  }, [selectedSite, timeRange]);

  async function fetchData() {
    setLoading(true);
    try {
      const startDate = daysAgoISO(timeRange);
      let query = supabase
        .from('search_console_queries')
        .select('*')
        .gte('date', startDate)
        .order('date', { ascending: false });

      if (selectedSite !== 'all') {
        query = query.eq('target_site', selectedSite);
      }

      const { data, error } = await query;
      if (error) throw error;
      setScData((data as SearchConsoleQuery[]) || []);
    } catch (err) {
      console.error('Error fetching Search Console data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function refreshSearchConsoleData() {
    setIsRefreshing(true);
    setProgress({ current: 0, total: 0, status: 'Initializing...' });

    try {
      // Build the full query set:
      // - brand terms per site (treated as brand for the matching site)
      // - base queries for everyone
      const perSiteBrandQueries: Array<{ site: string; query: string; type: 'brand'; volume: number }> = [];
      for (const site of TARGET_SITES) {
        const terms = BRAND_TERMS[site];
        terms.forEach((t) => {
          perSiteBrandQueries.push({ site, query: t, type: 'brand', volume: 12000 });
        });
      }

      // 30-day window
      const days = Array.from({ length: 30 }, (_, i) => i).reverse(); // oldest first

      // Pre-calc total tasks
      const totalTasks =
        days.length *
        TARGET_SITES.length *
        DEVICES.length *
        (perSiteBrandQueries.length / TARGET_SITES.length + BASE_QUERIES.length);

      setProgress({ current: 0, total: totalTasks, status: 'Generating data...' });

      const batchSize = 500;
      let buffer: SearchConsoleQuery[] = [];
      let completed = 0;

      // Helper to flush buffer
      const flush = async () => {
        if (buffer.length === 0) return;
        setProgress((p) => ({ ...p, status: `Upserting ${buffer.length} rows...` }));
        const { error } = await supabase
          .from('search_console_queries')
          .upsert(buffer as any, {
            onConflict: 'target_site,query,date,device,page',
          });
        if (error) throw error;
        buffer = [];
      };

      for (const day of days) {
        const date = daysAgoISO(day);

        for (const site of TARGET_SITES) {
          // Brand queries for THIS site
          const brandQueries = perSiteBrandQueries.filter((b) => b.site === site);

          // Full set = brand (for this site) + base queries
          const allQueries: Array<{ query: string; type: 'brand' | 'high-intent' | 'informational' | 'long-tail'; volume: number }> = [
            ...brandQueries,
            ...BASE_QUERIES,
          ];

          for (const device of DEVICES) {
            for (const q of allQueries) {
              // Calculate impressions, position, ctr, clicks, page
              const pos = expectedPositionFor(site, q, device);
              const ctr = ctrFromPosition(pos);
              const imps = impressionsFor(q.volume, device);
              const clicks = Math.max(0, Math.floor(imps * ctr * (0.9 + Math.random() * 0.2)));
              const page = pageForType(q.type);

              buffer.push({
                target_site: site,
                query: q.query,
                date,
                impressions: imps,
                clicks,
                ctr: clicks > 0 ? clicks / Math.max(1, imps) : ctr,
                position: Number(pos.toFixed(1)),
                page,
                device,
              });

              completed++;
              if (completed % 100 === 0) {
                setProgress({
                  current: completed,
                  total: totalTasks,
                  status: `Generating ${completed} / ${totalTasks}`,
                });
                // Yield to UI
                await new Promise((r) => setTimeout(r, 0));
              }

              if (buffer.length >= batchSize) {
                await flush();
                setProgress({
                  current: completed,
                  total: totalTasks,
                  status: `Persisted ${completed} / ${totalTasks}`,
                });
              }
            }
          }
        }
      }

      // Flush remainder
      await flush();
      setProgress({ current: totalTasks, total: totalTasks, status: 'Complete. Reloading data...' });

      // Refresh view
      await fetchData();
      setProgress({ current: totalTasks, total: totalTasks, status: 'Completed successfully.' });
    } catch (err) {
      console.error('Error during synthetic Search Console generation:', err);
      setProgress((p) => ({ ...p, status: 'Error during generation. See console.' }));
    } finally {
      setIsRefreshing(false);
    }
  }

  const totals = useMemo(() => {
    const rows = scData;
    const impressions = rows.reduce((s, r) => s + (r.impressions || 0), 0);
    const clicks = rows.reduce((s, r) => s + (r.clicks || 0), 0);
    const ctr = impressions > 0 ? clicks / impressions : 0;
    const avgPos = rows.length > 0 ? rows.reduce((s, r) => s + (r.position || 0), 0) / rows.length : 0;
    const uniqueQueries = new Set(rows.map((r) => r.query)).size;

    return {
      impressions,
      clicks,
      ctr,
      avgPos,
      uniqueQueries,
    };
  }, [scData]);

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BarChart2 className="text-blue-600" size={32} />
          <h1 className="text-3xl font-bold text-gray-900">Share of Search (Synthetic GSC)</h1>
        </div>
        <p className="text-gray-600">
          Generate and analyze demo-level Google Search Console metrics in-app. Use the button below to populate 30 days of data for target sites and devices.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Site</label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sites</option>
              {TARGET_SITES.map((s) => (
                <option key={s} value={s}>
                  {s}.com
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <button
              onClick={refreshSearchConsoleData}
              disabled={isRefreshing}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-white ${
                isRefreshing ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
              {isRefreshing ? 'Generating Demo Data...' : 'Refresh Search Console Data'}
            </button>

            {isRefreshing && (
              <div className="mt-3">
                <div className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">{progress.status}</span>
                  {progress.total > 0 && (
                    <span className="ml-2">
                      {progress.current} / {progress.total}
                    </span>
                  )}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width:
                        progress.total > 0
                          ? `${Math.min(100, Math.round((progress.current / progress.total) * 100))}%`
                          : '10%',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Total Impressions</span>
            <Activity className="text-blue-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{totals.impressions.toLocaleString()}</div>
          <div className="text-sm text-gray-500 mt-1">All sites and devices</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Total Clicks</span>
            <Activity className="text-green-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-green-600">{totals.clicks.toLocaleString()}</div>
          <div className="text-sm text-gray-500 mt-1">Engagement</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Avg CTR</span>
            <Activity className="text-purple-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{(totals.ctr * 100).toFixed(2)}%</div>
          <div className="text-sm text-gray-500 mt-1">Click-through rate</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm font-medium">Avg Position</span>
            <Activity className="text-orange-600" size={20} />
          </div>
          <div className="text-3xl font-bold text-gray-900">{totals.avgPos.toFixed(1)}</div>
          <div className="text-sm text-gray-500 mt-1">Lower = better</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Recent Search Performance</h2>
          <p className="text-sm text-gray-600">Showing up to 100 most recent rows</p>
        </div>

        {loading ? (
          <div className="p-8">
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          </div>
        ) : scData.length === 0 ? (
          <div className="p-12 text-center">
            <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Search Console Data</h3>
            <p className="text-gray-600 mb-4">
              Click the "Refresh Search Console Data" button above to generate demo data.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Site</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Query</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CTR</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scData.slice(0, 100).map((row, idx) => (
                  <tr key={`${row.date}-${row.target_site}-${row.query}-${row.device}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-700">{row.date}</td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{row.target_site}.com</td>
                    <td className="px-6 py-3 text-sm text-gray-700 capitalize">{row.device}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{row.query}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{row.page || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">{row.impressions.toLocaleString()}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">{row.clicks.toLocaleString()}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">{(row.ctr * 100).toFixed(2)}%</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right">{row.position.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 mt-1" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">About This Page</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• In-app demo generator replaces CLI scripts. No terminal steps are required.</li>
              <li>• Generates 30 days of synthetic Search Console data for 3 target sites and 2 devices.</li>
              <li>• Uses realistic CTR decay by position and device splits for impressions.</li>
              <li>• Safe for demos: does not require OAuth. Real GSC integration can be added server-side later.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
